import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowRight, CheckCircle2, Loader2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";

const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;

// Bandeja de cotizaciones de proveedor pendientes (Hub). Permite convertir a gasto
// ("Pago a proveedor") con un clic, arrastrando monto y proveedor.
export default function SupplierQuotesInbox() {
  const { lang } = useI18n();
  const es = lang === "es";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/hub/supplier-quotes").then((r) => setData(r.data)).catch(() => setData({ quotes: [], total: 0, count: 0 }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const convert = async (q) => {
    setBusy(q.id);
    const url = q.studio === "ops" ? `/operations/expenses/${q.id}` : `/studio/${q.studio}/finance/entries/${q.id}`;
    try {
      await api.patch(url, { type: "supplier_payment" });
      toast.success(es ? "Convertida a gasto (Pago a proveedor)" : "Converted to expense (Supplier payment)");
      load();
    } catch (e) {
      toast.error(es ? "No se pudo convertir" : "Couldn't convert");
    } finally { setBusy(null); }
  };

  if (!data || data.count === 0) return null;

  return (
    <div className="bg-white border border-navy/10 rounded-lg p-6" data-testid="hub-supplier-quotes-inbox">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gold/15 text-gold flex items-center justify-center shrink-0"><FileText className="w-4.5 h-4.5" /></div>
          <div>
            <div className="text-sm font-medium text-navy/80">{es ? "Cotizaciones de proveedor pendientes" : "Pending supplier quotes"}</div>
            <div className="text-xs text-navy/40">{data.count} {es ? "por revisar" : "to review"} · {money(data.total)} {es ? "estimado" : "estimated"}</div>
          </div>
        </div>
      </div>
      <div className="space-y-2 max-h-72 overflow-y-auto thin-scroll">
        {data.quotes.map((q) => (
          <div key={q.id} data-testid={`sq-inbox-item-${q.id}`} className="flex items-center gap-3 rounded-lg border border-navy/10 px-3.5 py-2.5 hover:border-violet/30 transition-colors group">
            <div className="min-w-0 flex-1">
              <div className="text-sm truncate flex items-center gap-1.5">
                {q.has_file && <Paperclip className="w-3 h-3 text-navy/30 shrink-0" />}
                {q.concept || (es ? "Cotización" : "Quote")}
                {q.supplier ? <span className="text-navy/45"> · {q.supplier}</span> : null}
              </div>
              <Link to={q.route} className="text-xs text-navy/40 hover:text-violet transition-colors inline-flex items-center gap-1" data-testid={`sq-inbox-link-${q.id}`}>
                {q.project} <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <span className="text-sm font-medium shrink-0 w-24 text-right">{money(q.amount)}</span>
            <button
              data-testid={`sq-inbox-convert-${q.id}`}
              onClick={() => convert(q)}
              disabled={busy === q.id}
              className="shrink-0 inline-flex items-center gap-1.5 text-xs rounded-full border border-navy/15 px-3 py-1.5 hover:border-emerald-500 hover:text-emerald-600 transition-colors disabled:opacity-50">
              {busy === q.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              {es ? "Convertir a gasto" : "Convert to expense"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
